import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { Drawer } from "@outpave/ds";
import { toast } from "react-toastify";
import ApiKeyTable from "./ApiKeyTable";
import ApiKeyCreateDrawer from "./ApiKeyCreateDrawer";
import ApiKeyEditDrawer from "./ApiKeyEditDrawer";
import ApiKeySecretRevealDialog from "./ApiKeySecretRevealDialog";
import ApiKeyMetricsChart from "./ApiKeyMetricsChart";
import ApiKeyScope from "enums/ApiKeyScope";
import ApiKeyStatus from "enums/ApiKeyStatus";
import { ApiKey, useApiKeys } from "hooks/useApiKeys";
import { toastMutationError } from "utils/toastMutationError";

interface ApiKeyManagementProps {
  scope: ApiKeyScope;
  ownerId: string;
  // The create drawer's flag belongs to the page, because both pages put the
  // button in their TablePage's primaryAction slot and the block has no header
  // row of its own to hang it on.
  createOpen: boolean;
  onCreateOpenChange: (open: boolean) => void;
}

/**
 * Reusable API key management surface. Mount in any settings tab: the caller
 * supplies the scope (COMPANY/PLATFORM) and the owner id, the rest of the
 * lifecycle (list/rotate/revoke/metrics) is self-contained.
 */
const ApiKeyManagement: React.FC<ApiKeyManagementProps> = ({
  scope,
  ownerId,
  createOpen,
  onCreateOpenChange,
}) => {
  const { t } = useTranslation();
  const {
    apiKeys,
    loading,
    error,
    createApiKey,
    revokeApiKey,
    rotateApiKey,
    updateApiKey,
  } = useApiKeys({ scope, ownerId });

  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [revealState, setRevealState] = useState<{
    apiKey: ApiKey;
    secret: string;
    rotated: boolean;
  } | null>(null);
  const [metricsKeyId, setMetricsKeyId] = useState<string | null>(null);
  // Cache totals per key id so the table can fill the "30-day calls" column
  // once the metrics drawer has loaded for at least one key.
  const [callTotals, setCallTotals] = useState<Record<string, number>>({});

  // Both handlers report the failure and then rethrow: the table closes its
  // confirmation only when the await resolves, so swallowing here dismissed the
  // dialog on a failed action and left no way back except the kebab.
  const handleRevoke = async (id: string) => {
    try {
      const revoked = await revokeApiKey(id);
      // revokeApiKey resolves false when the resolver found nothing to write,
      // so a promise that resolves is not a key that was deactivated. The
      // error carries no message on purpose: toastMutationError then falls
      // through to the translated one rather than surfacing English.
      if (!revoked) throw new Error();
      toast.success(t("ApiKeyDeactivatedToast"));
    } catch (err: unknown) {
      toastMutationError(err, t("ApiKeyDeactivateError"));
      throw err;
    }
  };

  const handleRotate = async (id: string) => {
    try {
      const result = await rotateApiKey(id);
      setRevealState({
        apiKey: result.apiKey,
        secret: result.secret,
        rotated: true,
      });
    } catch (err: unknown) {
      toastMutationError(err, t("ApiKeyRotateError"));
      throw err;
    }
  };

  // Both halves of this matter, and without either the drawer thrashes until
  // React bails with "Maximum update depth exceeded": the chart reports its
  // totals from an effect that depends on this callback, so an inline one
  // re-ran it on every parent render, and a spread that always built a new
  // object re-rendered the parent every time it did.
  const handleTotalsChange = useCallback(
    (totals: { calls: number; errors: number }) => {
      if (!metricsKeyId) return;
      setCallTotals((prev) =>
        prev[metricsKeyId] === totals.calls
          ? prev
          : { ...prev, [metricsKeyId]: totals.calls },
      );
    },
    [metricsKeyId],
  );

  const activeMetricsKey = metricsKeyId
    ? apiKeys.find((k) => k.id === metricsKeyId) || null
    : null;

  return (
    <>
      <ApiKeyTable
        apiKeys={apiKeys.filter((k) => k.status !== ApiKeyStatus.REVOKED)}
        loading={loading}
        loadError={!!error}
        callTotals={callTotals}
        onRevoke={handleRevoke}
        onRotate={handleRotate}
        onEdit={(key) => setEditingKey(key)}
        onViewMetrics={(id) => setMetricsKeyId(id)}
      />

      <ApiKeyCreateDrawer
        open={createOpen}
        scope={scope}
        ownerId={ownerId}
        onClose={() => onCreateOpenChange(false)}
        onCreate={createApiKey}
        onSecretReveal={(result) =>
          setRevealState({ ...result, rotated: false })
        }
      />

      <ApiKeyEditDrawer
        open={!!editingKey}
        apiKey={editingKey}
        onClose={() => setEditingKey(null)}
        onUpdate={updateApiKey}
      />

      <ApiKeySecretRevealDialog
        open={!!revealState}
        apiKey={revealState?.apiKey || null}
        secret={revealState?.secret || null}
        rotated={!!revealState?.rotated}
        onClose={() => setRevealState(null)}
      />

      <Drawer
        open={!!metricsKeyId}
        onClose={() => setMetricsKeyId(null)}
        title={
          activeMetricsKey
            ? t("Usage for {{name}}", { name: activeMetricsKey.name })
            : t("Usage")
        }
        footer={
          <Button
            variant="primary-outlined"
            onClick={() => setMetricsKeyId(null)}
          >
            {t("Close")}
          </Button>
        }
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t("ApiKeyUsageSubtitle")}
        </Typography>
        {metricsKeyId && (
          <ApiKeyMetricsChart
            apiKeyId={metricsKeyId}
            days={30}
            onTotalsChange={handleTotalsChange}
          />
        )}
      </Drawer>
    </>
  );
};

export default ApiKeyManagement;
